/**
 * Utilidad para generar PDF de presupuestos
 */

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface LineItem {
    id: string;
    description: string;
    quantity: number;
    unitPrice: number;
    discount: number;
    total: number;
}

interface QuoteData {
    quoteNumber: string;
    clientName: string;
    date: string;
    validUntil: string;
    lineItems: LineItem[];
    subtotal: number;
    tax: number;
    total: number;
    notes?: string;
    pestType?: string;
}

const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
    }).format(amount);
};

const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const months = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

export const generateQuotePDF = (quoteData: QuoteData): void => {
    // Crear documento PDF
    const doc = new jsPDF();

    // Configurar fuente
    doc.setFont("helvetica");

    // Encabezado de la empresa
    doc.setFontSize(20);
    doc.setTextColor(67, 56, 202); // Color primario
    doc.text("BIOSAM", 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("Control de Plagas y Desinfección", 14, 27);
    doc.text("Calle Principal, 123, 28001 Madrid", 14, 32);
    doc.text("Tel: +34 912 345 678 | Email: info@biosam.es", 14, 37);

    // Línea separadora
    doc.setDrawColor(67, 56, 202);
    doc.setLineWidth(0.5);
    doc.line(14, 42, 196, 42);

    // Título del presupuesto
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text("PRESUPUESTO", 14, 52);

    // Información del presupuesto y cliente
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("Número:", 14, 62);
    doc.setTextColor(0, 0, 0);
    doc.text(quoteData.quoteNumber, 40, 62);

    doc.setTextColor(100, 100, 100);
    doc.text("Cliente:", 14, 68);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text(quoteData.clientName, 40, 68);
    doc.setFont("helvetica", "normal");

    doc.setTextColor(100, 100, 100);
    doc.text("Fecha:", 14, 74);
    doc.setTextColor(0, 0, 0);
    doc.text(formatDate(quoteData.date), 40, 74);

    doc.setTextColor(100, 100, 100);
    doc.text("Válido hasta:", 14, 80);
    doc.setTextColor(0, 0, 0);
    doc.text(formatDate(quoteData.validUntil), 40, 80);

    // Tipo de plaga (si existe)
    if (quoteData.pestType) {
        doc.setTextColor(100, 100, 100);
        doc.text("Tipo de Plaga:", 14, 86);
        doc.setTextColor(5, 150, 105); // Verde
        doc.setFont("helvetica", "bold");
        doc.text(quoteData.pestType, 40, 86);
        doc.setFont("helvetica", "normal");
    }

    // Tabla de líneas de presupuesto (ajustar startY si hay tipo de plaga)
    const tableStartY = quoteData.pestType ? 96 : 90;
    const tableData = quoteData.lineItems.map((item) => [
        item.description,
        item.quantity.toString(),
        formatCurrency(item.unitPrice),
        `${item.discount}%`,
        formatCurrency(item.total),
    ]);

    autoTable(doc, {
        startY: tableStartY,
        head: [["Descripción", "Cantidad", "Precio Unit.", "Descuento", "Total"]],
        body: tableData,
        theme: "striped",
        headStyles: {
            fillColor: [67, 56, 202],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 10,
        },
        bodyStyles: {
            fontSize: 9,
        },
        columnStyles: {
            0: { cellWidth: 80 },
            1: { cellWidth: 25, halign: "center" },
            2: { cellWidth: 30, halign: "right" },
            3: { cellWidth: 25, halign: "center" },
            4: { cellWidth: 30, halign: "right" },
        },
    });

    // Obtener la posición Y después de la tabla
    const finalY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY || tableStartY;

    // Totales
    const totalsX = 130;
    let totalsY = finalY + 15;

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("Subtotal:", totalsX, totalsY);
    doc.setTextColor(0, 0, 0);
    doc.text(formatCurrency(quoteData.subtotal), 196, totalsY, { align: "right" });

    totalsY += 7;
    doc.setTextColor(100, 100, 100);
    doc.text("IVA (21%):", totalsX, totalsY);
    doc.setTextColor(0, 0, 0);
    doc.text(formatCurrency(quoteData.tax), 196, totalsY, { align: "right" });

    // Línea antes del total
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    totalsY += 3;
    doc.line(totalsX, totalsY, 196, totalsY);

    // Total final
    totalsY += 7;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(67, 56, 202);
    doc.text("TOTAL:", totalsX, totalsY);
    doc.text(formatCurrency(quoteData.total), 196, totalsY, { align: "right" });

    doc.setFont("helvetica", "normal");

    // Notas (si existen)
    if (quoteData.notes) {
        totalsY += 15;
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "bold");
        doc.text("Notas:", 14, totalsY);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 100, 100);

        // Dividir el texto en líneas si es muy largo
        const notesLines = doc.splitTextToSize(quoteData.notes, 180);
        doc.text(notesLines, 14, totalsY + 7);
    }

    // Pie de página
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
        "Este presupuesto tiene una validez limitada. Los precios pueden variar sin previo aviso.",
        14,
        pageHeight - 15
    );
    doc.text(
        `Generado el ${new Date().toLocaleDateString("es-ES")}`,
        196,
        pageHeight - 15,
        { align: "right" }
    );

    // Guardar el PDF
    doc.save(`Presupuesto-${quoteData.quoteNumber}.pdf`);
};
